import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { USER_ROLES } from "../../constants";
import { ROUTE_ACL } from "../../routeAcl";
import { ROLE_CONFIG } from "../../userRoleConfig";

/**
 * Returns the allowed routes for the given pathnames.
 * @param pathnames - The pathnames to check.
 * @returns The allowed routes and the loading state.
 */
export const useAllowedRoutes = (pathnames: string[]) => {
  const { assignedRoles, assignedCountries, isLoading } = useAuth();

  if (!assignedRoles) {
    // Likely means the user is not logged in
    return { allowedRoutes: [], isLoading };
  }

  if (assignedRoles.includes(USER_ROLES.SUPER_ADMIN)) {
    return { allowedRoutes: pathnames, isLoading };
  }

  const allowedRoutes = pathnames.filter((pathname) => {
    const config = ROUTE_ACL.find((route) => route.pathname === pathname);
    const allowedRoles = config?.allowedRoles || [];
    const roleAllowed =
      allowedRoles.length === 0 ||
      allowedRoles.some((role) => assignedRoles.includes(role));
    if (!roleAllowed) return false;

    const allowedCountries = config?.allowedCountries;
    if (!allowedCountries || allowedCountries.length === 0) return true;
    return allowedCountries.some((country) =>
      assignedCountries?.includes(country)
    );
  });

  return { allowedRoutes, isLoading };
};

const useAccessControl = (pathname: string) => {
  const { allowedRoutes, isLoading } = useAllowedRoutes([pathname]);
  return { hasAccess: allowedRoutes.includes(pathname), isLoading };
};

interface RoleBasedAccessControlProps {
  children: React.ReactNode;
  redirectBasedOnRole?: React.MutableRefObject<boolean>;
}

const RoleBasedAccessControl: React.FC<RoleBasedAccessControlProps> = ({
  children,
  redirectBasedOnRole,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, isLoading } = useAccessControl(location.pathname);
  const { assignedRoles, isLoading: isLoadingAuth } = useAuth();

  // Role-based redirection only after signing in
  useEffect(() => {
    if (redirectBasedOnRole?.current && !isLoadingAuth) {
      if (location.pathname === "/") {
        // only apply if the path is not specified
        let redirectPath = "/"; // Default fallback

        if (assignedRoles && assignedRoles.length > 0) {
          // Check roles in ROLE_CONFIG order (upper items have precedence)
          for (const [role, route] of Object.entries(ROLE_CONFIG)) {
            if (assignedRoles.includes(role)) {
              redirectPath = route;
              break;
            }
          }
        }

        navigate(redirectPath, { replace: true });
      }
      redirectBasedOnRole.current = false;
    }
  }, [
    redirectBasedOnRole,
    navigate,
    assignedRoles,
    location.pathname,
    isLoadingAuth,
  ]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default RoleBasedAccessControl;
